import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { isAvailableForCustomerType } from '@/lib/utils/user/isAvailableForCustomerType'
import type { CustomerType } from '@/store/types/user'
import useGlobalStore from '@/store/useGlobalStore'
import { ProductsGridCard } from './ProductsGridCard'

const getSortedProducts = ({
  products,
  customerType,
}: {
  products: CosmicProductType[] | undefined | null
  customerType: CustomerType
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  if (!products) return []

  const urlQueryParams = new URLSearchParams(window.location.search)

  const sortBy = (urlQueryParams.get('sortBy') ?? 'name') as
    | 'name'
    | 'price'
    | 'pv'
  const orderBy = (urlQueryParams.get('orderBy') ?? 'asc') as 'asc' | 'desc'

  const productsSorted = [...products].sort((a, b) => {
    let productsSortedReturn = 0

    const isInventoryData = sortBy === 'price' || sortBy === 'pv'

    if (isInventoryData) {
      const priceOrPv = sortBy === 'price' ? 'priceEach' : 'pvEach'

      const aPrice =
        a.variations?.[0]?.pricing?.levels.find(
          (level) => level.customer === customerType,
        )?.terms[priceOrPv] || 0

      const bPrice =
        b.variations?.[0]?.pricing?.levels.find(
          (level) => level.customer === customerType,
        )?.terms[priceOrPv] || 0

      productsSortedReturn =
        orderBy === 'asc' ? aPrice - bPrice : bPrice - aPrice
    } else if (sortBy === 'name') {
      const sortByKey = 'name'

      const aSortBy = a[sortByKey]

      const bSortBy = b[sortByKey]

      productsSortedReturn =
        orderBy === 'asc'
          ? aSortBy.localeCompare(bSortBy)
          : bSortBy.localeCompare(aSortBy)
    }

    return productsSortedReturn
  })

  return productsSorted
}

type Props = {
  products: CosmicProductType[] | null
}

export const ProductsGrid = ({ products }: Props) => {
  const {
    user: { customerType },
  } = useGlobalStore()

  const sortedProducts = getSortedProducts({
    products,
    customerType,
  })

  return (
    <>
      <div className="mb-10 grid w-full grid-cols-2 gap-2 px-2 sm:gap-4 sm:px-5 md:grid-cols-4">
        {sortedProducts?.map((product, index) => {
          if (
            !isAvailableForCustomerType({
              customerType,
              availableFor: product.availableFor,
            })
          ) {
            return null
          }

          return (
            <ProductsGridCard product={product} cardIndex={index} key={index} />
          )
        })}
      </div>
    </>
  )
}
